import { useAuthLogin } from '@lopo-tech/lopo-api'
import React, { useCallback, useState } from 'react'
import { Button } from 'src/components/button/button'
import InputField from 'src/components/forms/input_field/input_field'
import { CLASS_PREFIX } from 'src/constants'
import { textResources } from 'src/lang/de'
import { isNotEmpty, validateEmail } from 'src/utils/validation'
import { AUTH_CLS } from '../../auth/constants'
import './login_form.scss'

const cls = `${CLASS_PREFIX}login_form`

type IProps = {
  closeModal: () => void
  onForgotPassword: () => void
  onLoginSuccess?: () => void
  /** Seeds the field once, so a failed sign-in after registration does not ask for the address again. */
  initialEmail?: string
}

export const LoginForm: React.FC<IProps> = ({ closeModal, onForgotPassword, onLoginSuccess, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail ?? '')
  const [password, setPassword] = useState('')
  const [validEmail, setValidEmail] = useState(true)
  const [validPassword, setValidPassword] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { login } = useAuthLogin()

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    setValidEmail(true)
    setErrorMessage('')
  }, [])

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value)
    setValidPassword(true)
    setErrorMessage('')
  }, [])

  const handleSubmit = useCallback(() => {
    if (isLoading) return

    const emailValid = validateEmail(email)
    const passwordValid = isNotEmpty(password)

    if (!emailValid || !passwordValid) {
      setValidEmail(emailValid)
      setValidPassword(passwordValid)
      return
    }

    setIsLoading(true)
    login(email, password)
      .then(() => {
        if (onLoginSuccess) {
          onLoginSuccess()
        } else {
          closeModal()
        }
      })
      .catch((error) => {
        setErrorMessage(error instanceof Error ? error.message : textResources.shared.errorUnknown)
        setPassword('')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [email, password, login, isLoading, onLoginSuccess, closeModal])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.keyCode === 13) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  return (
    <div className={cls}>
      <div>
        <div className={`${AUTH_CLS}__header`}>{textResources.authentication.loginWelcomeTitle}</div>
        <div className={`${AUTH_CLS}__subheader`}>{textResources.authentication.loginWelcomeSubtitle}</div>
      </div>
      <InputField
        floatLabel={false}
        name="email"
        label={textResources.shared.email}
        placeholder={textResources.authentication.enterEmail}
        autoComplete="username"
        value={email}
        onChange={handleEmailChange}
        valid={validEmail}
        errorText={!validEmail ? textResources.shared.errorEmail : undefined}
      />
      <InputField
        floatLabel={false}
        name="password"
        label={textResources.shared.password}
        placeholder={textResources.authentication.enterPassword}
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={handlePasswordChange}
        valid={validPassword}
        errorText={!validPassword ? textResources.authentication.errorPassword : undefined}
        onKeyDown={handleKeyDown}
      />
      {errorMessage && <div className={`${cls}__error`}>{errorMessage}</div>}
      <button className={`${cls}__forgot-password`} onClick={onForgotPassword} type="button">
        {textResources.authentication.passwordForgot}
      </button>
      <Button
        size='xlarge'
        title={textResources.shared.login}
        disabled={isLoading}
        onClick={handleSubmit}
      />
    </div>
  )
}
